// src/infrastructure/api/errorMessages.ts

/**
 * User-friendly error messages for HTTP status codes
 */
export const HTTP_ERROR_MESSAGES: Record<number, string> = {
  // Client Errors (4xx)
  400: 'Invalid request. Please check your input and try again.',
  401: 'You need to log in to access this resource.',
  403: "You don't have permission to perform this action.",
  404: 'The requested resource could not be found.',
  405: 'This action is not allowed.',
  408: 'Request timed out. Please try again.',
  409: 'This action conflicts with the current state. Please refresh and try again.',
  410: 'This resource is no longer available.',
  413: 'The request is too large. Please reduce the file size.',
  415: 'This file type is not supported.',
  422: 'The provided data is invalid. Please check your input.',
  429: 'Too many requests. Please wait a moment and try again.',

  // Server Errors (5xx)
  500: 'Internal server error. Please try again later.',
  501: 'This feature is not currently available.',
  502: 'Bad gateway. Please try again later.',
  503: 'Service temporarily unavailable. Please try again later.',
  504: 'Gateway timeout. Please try again.',
  505: 'HTTP version not supported.'
};

/**
 * Special error messages for non-HTTP errors
 */
export const SPECIAL_ERROR_MESSAGES = {
  NETWORK: 'Network error. Please check your internet connection.',
  TIMEOUT: 'Request timed out. Please check your connection and try again.',
  UNKNOWN: 'An unexpected error occurred. Please try again.',
  VALIDATION: 'Please check your input and try again.',
  CANCELED: 'Request was canceled.'
} as const;

/**
 * Get user-friendly error message for HTTP status code
 */
export function getHttpErrorMessage(statusCode: number): string {
  return HTTP_ERROR_MESSAGES[statusCode] ?? SPECIAL_ERROR_MESSAGES.UNKNOWN;
}

/**
 * Get user-friendly error message from any error object
 */
export function getErrorMessage(error: unknown): string {
  // Handle ApiError (from our ApiClient)
  if (error && typeof error === 'object' && 'statusCode' in error) {
    const statusCode = (error as { statusCode: number }).statusCode;
    return getHttpErrorMessage(statusCode);
  }

  // Handle NetworkError
  if (error && typeof error === 'object' && 'name' in error && error.name === 'NetworkError') {
    return SPECIAL_ERROR_MESSAGES.NETWORK;
  }

  // Handle TimeoutError
  if (error && typeof error === 'object' && 'name' in error && error.name === 'TimeoutError') {
    return SPECIAL_ERROR_MESSAGES.TIMEOUT;
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    // Check for specific error messages we want to pass through
    if (error.message.includes('cancel')) {
      return SPECIAL_ERROR_MESSAGES.CANCELED;
    }

    // For development, show actual error message
    if (import.meta.env.DEV) {
      return error.message;
    }
  }

  // Default fallback
  return SPECIAL_ERROR_MESSAGES.UNKNOWN;
}

/**
 * Format validation errors into user-friendly message
 */
export function formatValidationErrors(errors: Record<string, string[]>): string {
  const errorMessages = Object.entries(errors)
    .map(([field, messages]) => {
      const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
      return `${fieldName}: ${messages.join(', ')}`;
    })
    .join('\n');

  return errorMessages || SPECIAL_ERROR_MESSAGES.VALIDATION;
}
